<template>
  <EditDialog
    :visible="showModal"
    :title="title"
    :width="600"
    :mask-closable="false"
    :sure-btn-text="sureBtnText"
    :padding="30"
    :loading="loading"
    :hide-footer="!hasBotConfigPermission && !!robotId"
    @close="handleDialogClose"
    @submit="saveSetting"
  >
    <div class="dialog-body">
      <div class="size-[72px] mx-auto mb-[32px]">
        <AgentAvatarPicker
          :key="botInfo.avatar_name"
          :icon="+botInfo.avatar_name"
          placement="bottom"
          :disabled="
            !hasBotConfigPermission &&
            !!robotId &&
            !['multi', 'multi-edit'].includes(currentType)
          "
          :show-edit-mask="
            hasBotConfigPermission ||
            !robotId ||
            ['multi', 'multi-edit'].includes(currentType)
          "
          @select="handleAvatarSubmit"
        />
      </div>
      <DynamicsForm
        :model="botInfo"
        :setting="setting"
        :label-width="110"
        class="bot-form"
        layout="labelTop"
        :disabled="
          !hasBotConfigPermission &&
          !!robotId &&
          !['multi', 'multi-edit'].includes(currentType)
        "
        :group-collapse="false"
        tips-placement="left"
        tips-icon="USolidHelpCircle"
      >
        <template #robot_name>
          <div class="bot-name-wrap">
            <u-input
              v-model="botInfo.robot_name"
              :maxlength="50"
              show-word-limit
              :disabled="!hasBotConfigPermission && !!robotId"
              :placeholder="
                $t(
                  'views.botSetting.BotForm.components.BaseForm.6cf186bf9bf862ddacd02c2942e93096'
                )
              "
            />
          </div>
        </template>
        <template #emb_model>
          <div>
            <u-select
              v-model="botInfo.emb_model"
              :placeholder="
                $t(
                  'views.botSetting.BotForm.components.BaseForm.ca2b34232ec279d65a6370a41b2921cd'
                )
              "
              :disabled="!!robotId"
              dropdown-menu-class="menu-list"
            >
              <u-option
                v-for="i in embModelList"
                :key="i.model_name"
                :value="i.model_name"
                :title="i.display_name"
              >
                <div class="flex min-w-0 w-full items-center gap-1">
                  <span class="min-w-0 flex-1 truncate">{{
                    i.display_name
                  }}</span>
                  <span
                    v-if="i.topping"
                    class="shrink-0"
                    style="color: var(--theme-color)"
                    >{{
                      $t(
                        'views.commonKnowledge.components.KnowledgeInfo.d7236d50d14d4ee03ce37b7fd0dd55d9'
                      )
                    }}</span
                  >
                </div>
              </u-option>
            </u-select>
          </div>
        </template>
        <template #emb_language>
          <u-select
            v-model="botInfo.emb_language"
            :placeholder="
              $t(
                'views.botSetting.components.AddAgent.80756c158e09b76a5d6da42a9a1de15d'
              )
            "
            :disabled="!!robotId || ['taskFlow-edit'].includes(currentType)"
            dropdown-menu-class="menu-list"
            multiple
            :multiple-limit="
              isI18n && !['taskFlow', 'taskFlow-edit'].includes(currentType)
                ? 3
                : 1
            "
            @change="handleEmbLanguageChange"
          >
            <u-option
              v-for="item in langList"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              {{ item.label }}
            </u-option>
          </u-select>
        </template>
        <template #emb_language_show>
          <u-input v-model="embLanguageShow" disabled />
        </template>
        <template #check_strategy>
          <u-select
            v-model="botInfo.check_strategy"
            :disabled="!!robotId"
            :placeholder="
              $t(
                'views.botSetting.components.AddAgent.ef30e0e1397040a4d76c78970ecddf42'
              )
            "
            dropdown-menu-class="menu-list"
          >
            <u-option
              v-for="item in languageDetectStrategyOptions"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              <span class="strategy-option-item">
                <span class="strategy-option-label">{{ item.label }}</span>
                <BotIcon
                  name="information-line"
                  size="16"
                  padding="0"
                  :tip="item.tip"
                  class="strategy-option-icon text-tertiary cursor-pointer"
                  @click.stop
                />
              </span>
            </u-option>
          </u-select>
        </template>
      </DynamicsForm>
    </div>
  </EditDialog>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { defineComponent, ref, computed } from 'vue';
import { DynamicsForm } from '@koi-design/vix-components';
import { message } from '@koi-design/uxd-ui';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { cloneDeep, omit } from 'lodash-es';
import {
  createRobotNew,
  getRobotDefaultConfigApi,
  getDefaultModelApi,
  getEmbeddingModelListApi
} from '@/api/robot';
import { getLastConversationIdApi } from '@/api/chat';
import {
  createMultiAgentApi,
  updateMultiAgentConfigApi
} from '@/api/multiAgent';
import {
  createTaskFlowAgentApi,
  getTaskFlowDataApi,
  updateTaskFlowApi
} from '@/api/taskFlowAgent';
import { mergeTaskFlowApiResponseToSchema } from '@V/taskFlowAgent/helper/flowSchemaFromApi';
import useFlowData from '@V/taskFlowAgent/useFlowData';
import { nanoid } from 'nanoid';
import { hasAuth } from '@/hooks/useAuth';
import { authMap } from '@/config/authConfig';
import AgentAvatarPicker from '@/components/AgentAvatar/picker.vue';
import { useBotInfo } from '@/views/botSetting/useData';
import { useMultiAgentInfo } from '@/views/multiAgent/useData';
import { languageDetectStrategyOptions } from '@/libs/enums';
import { applyAgentEmbeddingModelResolve } from '@/libs/resolveModelReplacement';

export default defineComponent({
  name: 'AddAgent',
  components: {
    DynamicsForm,
    AgentAvatarPicker
  },
  props: {
    robotId: {
      type: String,
      default: ''
    },
    botSettingInfo: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['submit'],
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const { taskFlowAgentInfo, setTaskFlowAgentInfo } = useFlowData();
    const { botInfo } = useBotInfo();
    const { multiAgentInfo } = useMultiAgentInfo();
    const homelandInfo = computed(() => store.getters.homelandInfo);
    const showModal = ref(false);
    const embLanguageShow = ref('');
    const title = ref(
      t('views.botSetting.components.AddAgent.011b9151b38b7bf479ab37a3b6488174')
    );
    const sureBtnText = ref(
      t('views.botSetting.components.AddAgent.2083691cadf8090e77fec6843d9f4381')
    );
    const hasBotConfigPermission = hasAuth(authMap.botConfig);
    console.log(hasBotConfigPermission.value, 'hasBotConfigPermission');
    const currentType = ref('add');
    const loading = ref(false);
    const langList = ref([]);
    const embModelList = ref([]);
    const width = computed(() => {
      return ['taskFlow', 'taskFlow-edit'].includes(currentType.value)
        ? '100%'
        : '50%';
    });
    const defaultLangOptions = ref([]);
    // Get intersection of model_list by model_name across all selected languages
    const getIntersectionModelList = selectedValues => {
      if (!selectedValues?.length) return [];
      const langItems = langList.value.filter(item =>
        selectedValues.includes(item.value)
      );
      const modelLists = langItems
        .map(item => item.model_list || [])
        .filter(list => list.length > 0);
      if (modelLists.length === 0) return [];
      if (modelLists.length === 1) return modelLists[0];
      const firstList = modelLists[0];
      const commonNames = new Set(
        firstList
          .filter(m =>
            modelLists.every(list =>
              list.some(x => x.model_name === m.model_name)
            )
          )
          .map(m => m.model_name)
      );
      return firstList.filter(m => commonNames.has(m.model_name));
    };

    // Edit: init langList + embModelList + defaultLangOptions from botInfo without overwriting fixed_language/emb_model
    const initEmbOptionsFromBotInfo = async () => {
      const res = await getEmbeddingModelListApi({
        homeland_id: homelandInfo.value.homeland
      });
      langList.value = res;
      const raw = botInfo.value.emb_language;
      const rawArr =
        typeof raw === 'string'
          ? raw
              .split(',')
              .map(s => s.trim())
              .filter(Boolean)
          : Array.isArray(raw)
          ? raw
          : raw
          ? [raw]
          : [];
      const selectedValues = rawArr.map(s => {
        const found = (res || []).find(
          item =>
            item.value === s || item.label === s || item.display_name === s
        );
        return found ? found.value : s;
      });
      defaultLangOptions.value = langList.value.filter(item =>
        selectedValues.includes(item.value)
      );
      embModelList.value = getIntersectionModelList(selectedValues);
    };
    const syncDefaultLanguageOptions = () => {
      const selected = botInfo.value.emb_language;
      const selectedValues = Array.isArray(selected)
        ? selected
        : selected
        ? [selected]
        : [];
      defaultLangOptions.value = langList.value.filter(item =>
        selectedValues.includes(item.value)
      );
      if (defaultLangOptions.value.length === 1) {
        botInfo.value.fixed_language = defaultLangOptions.value[0].value;
      } else if (
        botInfo.value.fixed_language &&
        !selectedValues.includes(botInfo.value.fixed_language)
      ) {
        botInfo.value.fixed_language = '';
      }
      embModelList.value = getIntersectionModelList(selectedValues);
      if (embModelList.value.length && botInfo.value.emb_model) {
        const inList = embModelList.value.some(
          m => m.model_name === botInfo.value.emb_model
        );
        if (!inList) {
          const topping = embModelList.value.find(i => i.topping);
          botInfo.value.emb_model = topping
            ? topping.model_name
            : embModelList.value[0].model_name;
        }
      } else if (embModelList.value.length && !botInfo.value.emb_model) {
        const topping = embModelList.value.find(i => i.topping);
        botInfo.value.emb_model = topping
          ? topping.model_name
          : embModelList.value[0].model_name;
      } else if (!embModelList.value.length) {
        botInfo.value.emb_model = '';
      }
    };
    const isI18n = computed(() => botInfo.value.is_i18n === true);
    const handleEmbLanguageChange = val => {
      const selectedValues = Array.isArray(val) ? val : val ? [val] : [];
      embModelList.value = getIntersectionModelList(selectedValues);
      botInfo.value.emb_model = '';
      if (embModelList.value.length) {
        const topping = embModelList.value.find(i => i.topping);
        botInfo.value.emb_model = topping
          ? topping.model_name
          : embModelList.value[0].model_name;
      }
      syncDefaultLanguageOptions();
    };
    const setting = ref([
      {
        label: t(
          'views.botSetting.BotForm.components.BaseForm.8fad2484e5932803fcd95722a5216cba'
        ),
        name: 'robot_name',
        type: 'slot',
        slot: 'robot_name',
        props: {
          maxlength: 50,
          showWordLimit: true,
          placeholder: t(
            'views.botSetting.BotForm.components.BaseForm.6cf186bf9bf862ddacd02c2942e93096'
          )
        },
        rules: [
          {
            required: true,
            message: '',
            trigger: 'change'
          }
        ]
      },
      {
        label: t(
          'views.botSetting.components.AddAgent.4ae4a5e52c90e6839cfc223e33a0fc49'
        ),
        name: 'description',
        type: 'textarea',
        props: {
          showWordLimit: true,
          maxlength: 300,
          rows: 2,
          placeholder: t(
            'views.botSetting.BotForm.components.BaseForm.fc491d318f255831d9375b43e63e4ff8'
          )
        }
      },
      {
        label: t(
          'views.botSetting.components.AddAgent.7b87334880d0465e5d8c0c118e178977'
        ),
        name: 'emb_language',
        type: 'slot',
        slot: 'emb_language',
        required: true,
        width: width.value,
        inline: true,
        class: 'form-item-inline form-item-lang',
        show: () => !['edit', 'multi', 'multi-edit'].includes(currentType.value)
      },
      {
        label: t(
          'views.botSetting.components.AddAgent.7b87334880d0465e5d8c0c118e178977'
        ),
        type: 'slot',
        width: '50%',
        inline: true,
        slot: 'emb_language_show',
        class: 'form-item-inline form-item-lang',
        show: () => ['edit'].includes(currentType.value)
      },
      {
        label: t(
          'views.botSetting.components.AddAgent.88da6d66ac873a964094caf46727847f'
        ),
        name: 'emb_model',
        type: 'slot',
        required: true,
        width: '50%',
        inline: true,
        class: 'form-item-inline form-item-emb-model',
        tips: t(
          'views.botSetting.components.AddAgent.5f6423bef70920647726734e6ac7f286'
        ),
        show: () =>
          !['multi', 'multi-edit', 'taskFlow', 'taskFlow-edit'].includes(
            currentType.value
          )
      },
      {
        label: t(
          'views.botSetting.components.AddAgent.fc5cc182e033e56c1c42626ac3ad3fc4'
        ),
        name: 'fixed_language',
        type: 'select',
        required: true,
        options: defaultLangOptions,
        width: '50%',
        inline: true,
        disabled: !!props.robotId,
        props: {
          dropdownMenuClass: 'menu-list',
          placeholder: t(
            'views.botSetting.components.AddAgent.8b5ed1b52dcf0e3f152bcfbe194ec3d3'
          )
        },
        class: 'form-item-inline form-item-lang',
        show: () =>
          !['multi', 'multi-edit', 'taskFlow', 'taskFlow-edit'].includes(
            currentType.value
          ) && isI18n.value
      },
      {
        label: t(
          'views.botSetting.components.AddAgent.29ea7f7bfc9b4776ca94e94b4b912430'
        ),
        name: 'check_strategy',
        type: 'slot',
        slot: 'check_strategy',
        required: true,
        width: '50%',
        inline: true,
        rules: [
          {
            required: true,
            type: 'number',
            message: t(
              'views.botSetting.components.AddAgent.ed6ebdbccc71e8cb6a7ca093992a901f'
            ),
            trigger: 'change'
          }
        ],
        class: 'form-item-inline form-item-emb-model',
        show: () =>
          !['multi', 'multi-edit', 'taskFlow', 'taskFlow-edit'].includes(
            currentType.value
          ) && isI18n.value
      }
    ]);
    const syncFormItemWidth = () => {
      const langItem = setting.value.find(item => item.name === 'emb_language');
      if (langItem) {
        langItem.width = width.value;
        langItem.disabled =
          !!props.robotId || currentType.value === 'taskFlow-edit';
      }
    };
    const getEmbeddingModelList = async () => {
      const res = await getEmbeddingModelListApi({
        homeland_id: homelandInfo.value.homeland
      });
      langList.value = res;
      if (!botInfo.value.emb_language) {
        const isDefaultLang = res.find(i => i.is_default) || res[0];
        botInfo.value.emb_language = isDefaultLang.value;
        embModelList.value = isDefaultLang.model_list;
        syncDefaultLanguageOptions();

        // if has topping model, use it
        const toppingModel = embModelList.value.find(item => item.topping);
        if (toppingModel) {
          botInfo.value.emb_model_name = toppingModel.model_name;
        } else {
          botInfo.value.emb_model_name = embModelList.value[0].model_name;
        }
      } else {
        const selected = botInfo.value.emb_language;
        const selectedValues = Array.isArray(selected)
          ? selected
          : selected
          ? [selected]
          : [];
        embModelList.value = getIntersectionModelList(selectedValues);
        syncDefaultLanguageOptions();
      }
    };
    const avatarSelectRef = ref(null);
    const handleAvatarSubmit = data => {
      botInfo.value.avatar_name = data.icon;
    };
    const init = async () => {
      const [config, defaultModel] = await Promise.all([
        getRobotDefaultConfigApi(),
        currentType.value === 'add'
          ? getDefaultModelApi().catch(() => ({}))
          : Promise.resolve({})
      ]);
      const res = {
        ...config,
        ...(defaultModel?.data || defaultModel)
      };
      if (currentType.value === 'add') {
        res.deep_thinking_enable = false;
        res.deep_thinking_level = '';
      }
      res.tip_message_variable = res.tip_message_variable.map(i => ({
        ...i,
        id: nanoid()
      }));
      botInfo.value = res;
      syncDefaultLanguageOptions();
    };
    const show = async type => {
      currentType.value = type;
      console.log('type', type);
      syncFormItemWidth();
      if (type === 'add') {
        title.value = t(
          'views.botSetting.components.AddAgent.011b9151b38b7bf479ab37a3b6488174'
        );
        sureBtnText.value = t(
          'views.botSetting.components.AddAgent.2083691cadf8090e77fec6843d9f4381'
        );
        init();
      } else if (type === 'edit') {
        title.value = t(
          'views.botSetting.components.AddAgent.3f886f75ed1997b1aad273d3fa2490fb'
        );
        sureBtnText.value = t(
          'views.botSetting.components.AddAgent.7779910318cde9c3f081080b26db0488'
        );
        embLanguageShow.value = botInfo.value.emb_language_name;
        await initEmbOptionsFromBotInfo();
        await applyAgentEmbeddingModelResolve(
          botInfo.value,
          embModelList
        );
      } else if (type === 'multi') {
        currentType.value = 'multi';
        title.value = t(
          'views.botSetting.components.AddAgent.24e08d4a3f93dc1c14d7eed2b7cb8841'
        );
        sureBtnText.value = t(
          'views.botSetting.components.AddAgent.2083691cadf8090e77fec6843d9f4381'
        );
        init();
        showModal.value = true;
        return;
      } else if (type === 'multi-edit') {
        currentType.value = 'multi-edit';
        botInfo.value = {
          ...botInfo.value,
          robot_name: props.botSettingInfo.name,
          description: props.botSettingInfo.desc,
          avatar_name: props.botSettingInfo.avatar_name
        };
        title.value = t(
          'views.botSetting.components.AddAgent.ea648f1f59ca9587f6746861ea611800'
        );
        sureBtnText.value = t(
          'views.botSetting.components.AddAgent.7779910318cde9c3f081080b26db0488'
        );
        showModal.value = true;
        return;
      } else if (type === 'taskFlow') {
        currentType.value = 'taskFlow';
        title.value = t(
          'views.botSetting.components.AddAgent.7ded5bee07cc70c446e8893efa6c442b'
        );
        sureBtnText.value = t(
          'views.botSetting.components.AddAgent.2083691cadf8090e77fec6843d9f4381'
        );
        init();
        showModal.value = true;
      } else if (type === 'taskFlow-edit') {
        currentType.value = 'taskFlow-edit';
        botInfo.value = {
          ...botInfo.value,
          robot_name: props.botSettingInfo.name,
          description: props.botSettingInfo.desc,
          avatar_name: props.botSettingInfo.avatar_name
        };
        title.value = t(
          'views.botSetting.components.AddAgent.761c6f8a06c9e95d11fa3d9a1dc9be74'
        );
        sureBtnText.value = t(
          'views.botSetting.components.AddAgent.7779910318cde9c3f081080b26db0488'
        );
        showModal.value = true;
      }
      // Edit / taskFlow-edit: no second fetch (avoids clearing emb_model; task flow edit does not need list init here)
      if (!['edit'].includes(type)) {
        getEmbeddingModelList();
      }
      syncFormItemWidth();
      showModal.value = true;
    };
    const handleDialogClose = () => {
      showModal.value = false;
    };
    const langSettingRef = ref(null);
    const handleOpenLangSetting = () => {
      langSettingRef.value.show(botInfo.value);
    };
    const saveSetting = async () => {
      if (!botInfo.value.robot_name?.trim()) {
        message.error(
          t(
            'views.botSetting.components.AddAgent.5dc223810a59514bc19ac42e534b741b'
          )
        );
        return;
      }
      if (currentType.value === 'add') {
        const hasEmbLanguage = Array.isArray(botInfo.value.emb_language)
          ? botInfo.value.emb_language.length > 0
          : !!botInfo.value.emb_language;
        const requiredTip = t(
          'views.botSetting.components.AddAgent.421b3eb5b8aa2ce860431081650aad56'
        );
        if (!hasEmbLanguage) {
          message.error(
            t(
              'views.botSetting.components.AddAgent.07d2cbcb2dffdeab16c01c7d4cb1c4cc',
              [requiredTip]
            )
          );
          return;
        }
        if (!botInfo.value.emb_model || !botInfo.value.emb_model.trim()) {
          message.error(
            t(
              'views.botSetting.components.AddAgent.7bb08349ffa0b26a581701c84b73d12e',
              [requiredTip]
            )
          );
          return;
        }
        if (isI18n.value) {
          if (!botInfo.value.fixed_language) {
            message.error(
              t(
                'views.botSetting.components.AddAgent.f0b6e2d8d4f524c8f9d9a52075347bad',
                [requiredTip]
              )
            );
            return;
          }
          if (!botInfo.value.check_strategy) {
            message.error(
              t(
                'views.botSetting.components.AddAgent.df3ad6ab53081cfbda1dda73f8063777',
                [requiredTip]
              )
            );
            return;
          }
        }
      }
      if (currentType.value === 'multi') {
        loading.value = true;
        try {
          const res = await createMultiAgentApi({
            ...botInfo.value,
            avatar_name: botInfo.value.avatar_name.toString(),
            name: botInfo.value.robot_name,
            desc: botInfo.value.description,
            homeland_id: homelandInfo.value.homeland
          });
          router.push(`/multiAgent/${res.multi_agent_id}`);
          showModal.value = false;
          loading.value = false;
        } catch (error) {
          message.error(error);
          loading.value = false;
        }
        return;
      }
      if (currentType.value === 'multi-edit') {
        loading.value = true;
        try {
          const res = await updateMultiAgentConfigApi({
            ...botInfo.value,
            avatar_name: botInfo.value.avatar_name.toString(),
            multi_agent_id: props.botSettingInfo.multi_agent_id,
            homeland_id: homelandInfo.value.homeland,
            name: botInfo.value.robot_name,
            desc: botInfo.value.description
          });
          console.log('res', res);
          // Update multiAgentInfo with the new values
          if (multiAgentInfo.value) {
            multiAgentInfo.value = {
              ...multiAgentInfo.value,
              name: botInfo.value.robot_name,
              desc: botInfo.value.description,
              avatar_name: botInfo.value.avatar_name
            };
          }
          message.success(
            t(
              'views.botSetting.components.AddAgent.f751efa72fd1d87935833fcd48bdaa92'
            )
          );
          showModal.value = false;
          loading.value = false;
        } catch (error) {
          message.error(error);
          loading.value = false;
        }
        return;
      }
      if (currentType.value === 'taskFlow') {
        loading.value = true;
        try {
          const res = await createTaskFlowAgentApi({
            ...botInfo.value,
            avatar_name: botInfo.value.avatar_name.toString(),
            name: botInfo.value.robot_name,
            desc: botInfo.value.description,
            homeland_id: homelandInfo.value.homeland
          });
          const detailRes = await getTaskFlowDataApi({
            robot_id: res.robot_id,
            homeland_id: homelandInfo.value.homeland
          });
          router.push({
            path: '/taskFlowAgent',
            query: {
              robotId: detailRes.agent_id,
              flowId: detailRes?.workflow?.flow_uuid
            }
          });
          showModal.value = false;
          loading.value = false;
        } catch (error) {
          message.error(error);
          loading.value = false;
          router.push({
            path: '/taskFlowAgent',
            query: {
              robotId: '1'
            }
          });
        }
        return;
      }
      if (currentType.value === 'taskFlow-edit') {
        loading.value = true;
        try {
          const robotIdVal = props.robotId || route.query.robotId;
          if (robotIdVal == null || robotIdVal === '') {
            message.error(
              t(
                'views.botMedia.components.GenerateLink.d638789c74e22550e7f9e2f912dd064d'
              )
            );
            loading.value = false;
            return;
          }
          const hid = homelandInfo.value.homeland;
          const flowRaw = await getTaskFlowDataApi({
            robot_id: Number(robotIdVal),
            homeland_id: hid
          });
          if (!flowRaw || typeof flowRaw !== 'object') {
            message.error(
              t('views.agentManagement.index.c764cf03cf0297f777fc608adb54014d')
            );
            loading.value = false;
            return;
          }
          const avatarStr = botInfo.value.avatar_name.toString();
          const patchedRaw = {
            ...flowRaw,
            name: botInfo.value.robot_name,
            robot_name: botInfo.value.robot_name,
            desc: botInfo.value.description,
            description: botInfo.value.description,
            avatar_name: avatarStr
          };
          const schema = mergeTaskFlowApiResponseToSchema(patchedRaw);
          const triggerList = Array.isArray(schema.trigger_list)
            ? schema.trigger_list
            : [];
          const isTrigger =
            schema.is_trigger !== undefined && schema.is_trigger !== null
              ? !!schema.is_trigger
              : triggerList.length > 0;
          const { workflow_nodes, config } = schema;
          const topLevelFields = omit(schema, [
            'nodes',
            'edges',
            'workflow',
            'workflow_nodes',
            'config',
            'flow_uuid'
          ]);
          const params = cloneDeep({
            intention_id: route.query.intentionId ?? route.params.id,
            robot_id: Number(robotIdVal),
            homeland_id: hid,
            ...topLevelFields,
            trigger_list: triggerList,
            is_trigger: isTrigger,
            workflow: {}
          });
          params.workflow.workflow_nodes = Array.isArray(workflow_nodes)
            ? workflow_nodes
            : [];
          params.workflow.config =
            config && typeof config === 'object' ? config : {};
          params.workflow.flow_uuid =
            schema.flow_uuid || String(route.query.flowId || '');
          params.workflow.edges = cloneDeep(schema.edges);
          params.workflow.nodes = cloneDeep(schema.nodes);
          const res = await updateTaskFlowApi(params);
          if (!res) {
            loading.value = false;
            return;
          }
          if (
            taskFlowAgentInfo.value &&
            typeof taskFlowAgentInfo.value === 'object'
          ) {
            setTaskFlowAgentInfo({
              ...taskFlowAgentInfo.value,
              name: botInfo.value.robot_name,
              desc: botInfo.value.description,
              description: botInfo.value.description,
              avatar_name: botInfo.value.avatar_name
            });
          }
          showModal.value = false;
        } catch (error) {
          message.error(error);
        } finally {
          loading.value = false;
        }
        return;
      }
      if (!props.robotId) {
        botInfo.value.homeland_id = homelandInfo.value.homeland;
        loading.value = true;
        try {
          // Prepare submission data based on language mode
          const submitData = {
            ...botInfo.value,
            intent_embedding_language: botInfo.value.emb_language,
            intent_embedding_model_name: botInfo.value.emb_model
          };

          // Check if emb_language is an array (multi-language mode)
          const embLanguage = submitData.emb_language;
          const isMultiLanguage =
            Array.isArray(embLanguage) && embLanguage.length > 1;
          if (isMultiLanguage) {
            // Multi-language: backend needs check_language = true
            submitData.check_language = true;
            // Multi-language mode: use welcome_message_i18n, remove welcome_message
            const i18nLang = localStorage.getItem('language') || 'zh_CN';
            const getEmbLanguageFromI18n = lang => {
              if (lang.startsWith('zh')) return 'zh';
              if (lang.startsWith('en')) return 'en';
              if (lang.startsWith('yue')) return 'yue';
              return 'zh';
            };
            const currentLang = getEmbLanguageFromI18n(i18nLang);
            const welcomeMessageI18n = {};
            embLanguage.forEach(lang => {
              if (lang === currentLang) {
                welcomeMessageI18n[lang] = botInfo.value.welcome_message || '';
              } else {
                welcomeMessageI18n[lang] = '';
              }
            });
            submitData.welcome_message_i18n = welcomeMessageI18n;
          }
          const data = await createRobotNew(submitData, props.robotId);
          if (!data) {
            loading.value = false;
            return;
          }
          const { id } = data.robot;
          store.commit('setCurrentBot', {
            id,
            agent_type: 1
          });
          const res = await getLastConversationIdApi({
            robot_id: id,
            message_source: 'web_preview'
          });
          router.push({
            path: '/botSetting',
            query: {
              id,
              type: currentType.value,
              sessionId: res.last_segment_code
            }
          });
          showModal.value = false;
          loading.value = false;
        } catch (error) {
          message.error(error);
          loading.value = false;
        }
      }
      showModal.value = false;
    };
    return {
      showModal,
      loading,
      show,
      saveSetting,
      embLanguageShow,
      handleDialogClose,
      setting,
      title,
      sureBtnText,
      avatarSelectRef,
      handleAvatarSubmit,
      hasBotConfigPermission,
      langSettingRef,
      handleOpenLangSetting,
      embModelList,
      currentType,
      botInfo,
      languageDetectStrategyOptions,
      isI18n,
      langList,
      handleEmbLanguageChange
    };
  }
});
</script>

<style lang="less" scoped>
.strategy-option-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.strategy-option-label {
  min-width: 0;
}

.strategy-option-icon {
  flex-shrink: 0;
}
</style>
<style lang="less" scoped>
.dialog-body {
  .bot-icon {
    height: 72px;
    position: relative;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 32px;

    > div {
      width: 72px;
      height: 72px;
    }
    .edit-icon {
      position: absolute;
      right: 0;
      bottom: 0;
      height: 14px;
      line-height: 14px;
      background-color: var(--color-filled-background-popover);
      color: var(--color-text-icon-invert);
      font-size: 10px;
      text-align: center;
      width: 25px;
      border-radius: 4px 0 4px 0;
    }
  }
  .bot-name-wrap {
    display: flex;

    .bot-icon {
      margin-right: 8px;
      width: 36px;
      height: 36px;
      border-radius: 8px;
      overflow: hidden;
      position: relative;
      flex-shrink: 0;

      .edit-icon {
        position: absolute;
        right: 0;
        bottom: 0;
        height: 14px;
        line-height: 14px;
        background-color: var(--color-filled-background-popover);
        color: var(--color-text-icon-invert);
        font-size: 10px;
        text-align: center;
        width: 25px;
        border-radius: 4px 0 4px 0;
      }
    }
  }

  :deep(.form-item-lang) {
    .uui-form-item-content {
      margin-left: 0;
    }
  }

  :deep(.form-item-emb-model) {
    .uui-form-item-label {
      margin-left: 12px;
    }
  }
}
</style>
